import { useEffect, useState } from "react";

import { PaymentsPayoutService } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { PaymentsPayinService } from "@/modules/dashboard/payins/services/paymentsPayin.service";

import type { ActivityDirection, ActivityRow } from "../types/paymentsActivity.types";
import { adaptPayinRow, adaptPayoutRow } from "../utils/activityAdapters";

export interface UseSelectedOperationResult {
  row: ActivityRow | null;
  /** Resolving an id that is not on the current page — the caller shows nothing yet, not an empty drawer. */
  isLoading: boolean;
  /** The id could not be read: deleted, wrong direction, or another organization's operation (404). */
  notFound: boolean;
}

interface RowSnapshot {
  key: string;
  row: ActivityRow | null;
  failed: boolean;
}

/**
 * Resolves the operation the URL points at into a row the drawer can render.
 *
 * The row usually already sits on the loaded page — that is the normal click-a-row path, and it costs no
 * request. A deep link is the case that needs work: the operation may be on another page, behind other
 * filters, or older than the current window, so it is fetched by id from its own endpoint (`GET /:id`,
 * org-scoped server-side — another organization's id is a 404, never a leak).
 */
export const useSelectedOperation = (
  direction: ActivityDirection,
  operationId: string | null,
  rows: ActivityRow[],
): UseSelectedOperationResult => {
  const [snapshot, setSnapshot] = useState<RowSnapshot | null>(null);

  const key = operationId ? `${direction}|${operationId}` : null;
  const fromPage = operationId
    ? (rows.find((row) => row.id === operationId && row.direction === direction) ?? null)
    : null;
  // Derived, not synchronized: a snapshot of another operation simply stops matching the key.
  const current = snapshot?.key === key ? snapshot : null;
  const needsFetch = key !== null && fromPage === null;

  useEffect(() => {
    if (!operationId || !needsFetch) return;
    const snapshotKey = `${direction}|${operationId}`;
    let cancelled = false;
    const request =
      direction === "payins"
        ? PaymentsPayinService.getPayin(operationId).then((res) => adaptPayinRow(res.data))
        : PaymentsPayoutService.getPayout(operationId).then((res) => adaptPayoutRow(res.data));

    request
      .then((row) => {
        if (!cancelled) setSnapshot({ key: snapshotKey, row, failed: false });
      })
      .catch(() => {
        if (!cancelled) setSnapshot({ key: snapshotKey, row: null, failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [direction, operationId, needsFetch]);

  return {
    // The fetched row wins when there is one: it is stable across list refreshes, so the drawer does not
    // restart its own polling every time the page behind it reloads.
    row: current?.row ?? fromPage,
    isLoading: needsFetch && current === null,
    notFound: current?.failed ?? false,
  };
};
